import {
  doc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../../../firebaseConfig';
import { showSuccessToast, showErrorToast } from '../../../../../utils/toastUtils';
import { markNotificationAsRead } from '../utils.js';

export const handleFollowNotificationClick = async (notification, navigate, additionalData = {}) => {
  try {
    // Mark as read
    await markNotificationAsRead(notification.id);

    // Navigate to the follower's profile
    if (notification.actorId) {
      const currentUserId = additionalData.currentUserId || null;
      if (currentUserId && currentUserId === notification.actorId) {
        navigate('/profile');
      } else {
        navigate(`/profile/${notification.actorId}`);
      }
    } else if (notification.actionUrl) {
      navigate(notification.actionUrl);
    } else {
      navigate('/home');
    }

    return { success: true };
  } catch (error) {
    console.error('Error handling follow notification:', error);
    return { success: false, error };
  }
};

export const handleFollowRequestAccept = async (notification, currentUserId) => {
  try {
    if (!currentUserId) throw new Error('Missing current user id');

    // Locate the follow request document
    let requestId = notification.followRequestId || null;
    let requestDoc = null;

    if (requestId) {
      const ref = doc(db, 'follow_requests', requestId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        requestDoc = { id: snap.id, ...snap.data() };
      }
    }

    if (!requestDoc) {
      const requestsRef = collection(db, 'follow_requests');
      const q = query(
        requestsRef,
        where('fromUserId', '==', notification.actorId),
        where('toUserId', '==', currentUserId),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        requestDoc = { id: d.id, ...d.data() };
      }
    }

    if (!requestDoc) {
      showErrorToast('Follow request not found or already handled.');
      return { success: false, error: 'not_found' };
    }

    const requestRef = doc(db, 'follow_requests', requestDoc.id);
    await updateDoc(requestRef, {
      status: 'accepted',
      updatedAt: serverTimestamp(),
    });

    // Mark the notification as read
    await markNotificationAsRead(notification.id);

    showSuccessToast('Follow request accepted');
    return { success: true };
  } catch (error) {
    console.error('Error accepting follow request:', error);
    showErrorToast('Failed to accept follow request. Please try again.');
    return { success: false, error };
  }
};

export const handleFollowRequestReject = async (notification, currentUserId) => {
  try {
    if (!currentUserId) throw new Error('Missing current user id');

    let requestId = notification.followRequestId || null;
    let requestDoc = null;

    if (requestId) {
      const ref = doc(db, 'follow_requests', requestId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        requestDoc = { id: snap.id, ...snap.data() };
      }
    }

    if (!requestDoc) {
      const requestsRef = collection(db, 'follow_requests');
      const q = query(
        requestsRef,
        where('fromUserId', '==', notification.actorId),
        where('toUserId', '==', currentUserId),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        requestDoc = { id: d.id, ...d.data() };
      }
    }

    if (!requestDoc) {
      showErrorToast('Follow request not found or already handled.');
      return { success: false, error: 'not_found' };
    }

    const requestRef = doc(db, 'follow_requests', requestDoc.id);
    await updateDoc(requestRef, {
      status: 'rejected',
      updatedAt: serverTimestamp(),
    });

    await markNotificationAsRead(notification.id);

    showSuccessToast('Follow request rejected');
    return { success: true };
  } catch (error) {
    console.error('Error rejecting follow request:', error);
    showErrorToast('Failed to reject follow request. Please try again.');
    return { success: false, error };
  }
};
